/**
 * Session state for the web app.
 *
 * The session token lives in an httpOnly cookie set by the API (SLP-NodeJSBackend
 * lib/authCookie.js). Page scripts cannot read it — that is the point: an XSS can
 * no longer steal it. The browser attaches it to every API call on its own
 * (axios `withCredentials`), so the app never touches the token. To know whether
 * someone is signed in, the app asks the API (GET /auth/me) once on load and
 * tracks the answer here.
 *
 * CSRF: the API also sets a readable XSRF cookie; axios echoes it in the
 * X-XSRF-TOKEN header on every credentialed request (configured in api/index.ts).
 */
import axios from 'axios';
import Cookies from 'js-cookie';
import config from '../config';
import { removeUserData } from './utils';

// Where the token used to live, readable by JS. Only read here, once, to move an
// existing session into the httpOnly cookie; then deleted.
const LEGACY_TOKEN_COOKIE = 'slpfront_db_token';

let signedIn = false;

/** True once the API has confirmed a session (on load, or right after login). */
export const hasSession = (): boolean => signedIn;

/** Call after a login response: the API has just set the session cookie. */
export const markSignedIn = (): void => {
  signedIn = true;
};

/** Forget the session locally (e.g. after a 401 — the cookie is already dead). */
export const clearSessionLocal = (): void => {
  signedIn = false;
  removeUserData();
  Cookies.remove(LEGACY_TOKEN_COOKIE);
};

/** Sign out: the API clears the httpOnly cookie (JS cannot). */
export const endSession = async (): Promise<void> => {
  clearSessionLocal();
  try {
    await axios.post(`${config.API_BASE_URL}/auth/logout`, {}, { withCredentials: true });
  } catch (e) {
    // Already signed out as far as this page is concerned.
  }
};

/**
 * Run once before the app renders.
 *
 * 1. Transition: a browser signed in before the cookie existed still holds its
 *    token in the old readable cookie. Hand it to the API once so the same token
 *    moves into the httpOnly cookie, then delete the readable copy. Nobody is
 *    signed out at cutover. (Remove with the backend's Step 7.)
 * 2. Ask the API whether a session exists.
 */
export const initSession = async (): Promise<boolean> => {
  const legacy = Cookies.get(LEGACY_TOKEN_COOKIE);
  if (legacy) {
    try {
      await axios.post(`${config.API_BASE_URL}/auth/upgrade-session`, {}, {
        withCredentials: true,
        headers: { Authorization: `jwt ${legacy}` },
      });
    } catch (e) {
      // Expired or rejected — the probe below decides.
    }
    Cookies.remove(LEGACY_TOKEN_COOKIE);
  }
  try {
    await axios.get(`${config.API_BASE_URL}/auth/me`, { withCredentials: true });
    signedIn = true;
  } catch (e) {
    signedIn = false;
  }
  return signedIn;
};
